import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import Popper from '@mui/material/Popper';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { Z_INDEX_MAP } from '@grnhse/seedling/lib/birch/utils/constants';
import CompanyInfoCard from 'shared/components/company_info/company_info_card';
import { useCompanyProfile } from 'shared/components/company_info/use_company_profiles';

const OPEN_DELAY_MS = 140;
const CLOSE_DELAY_MS = 300;

const CARD_FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

let cardCounter = 0;

const Trigger = styled.span`
  text-decoration: underline;
  text-decoration-color: ${FullPalette.GREY_700};
  text-underline-offset: 2px;

  &:hover,
  &:focus-visible {
    text-decoration-color: ${FullPalette.EVERGREEN_700};
  }
`;

type Props = {
  companyName: string;
};

type OpenCardProps = {
  companyName: string;
  cardId: string;
  anchor: HTMLElement;
  cardRef: React.Ref<HTMLDivElement>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onBlur: (event: React.FocusEvent<HTMLElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
};

// Mounted only while open: a list can carry hundreds of triggers, and each closed one
// would otherwise keep its own query observer and Popper re-rendering with the list.
const OpenCard = ({
  companyName,
  cardId,
  anchor,
  cardRef,
  ...handlers
}: OpenCardProps) => {
  const { profile, isLoading, isUnavailable } = useCompanyProfile(
    companyName,
    true
  );

  return (
    // Portalled out from under the caller's .pii-blur, so it masks itself.
    <Popper
      ref={cardRef}
      id={cardId}
      className="pii-blur"
      data-amp-mask
      open
      anchorEl={anchor}
      placement="right-start"
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ]}
      style={{ zIndex: Z_INDEX_MAP.tooltip }}
      {...handlers}
    >
      <CompanyInfoCard
        companyName={companyName}
        profile={profile}
        isLoading={isLoading}
        isUnavailable={isUnavailable}
      />
    </Popper>
  );
};

const CompanyInfoHover = ({ companyName }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Escape hands focus back to the trigger, whose focus handler would otherwise
  // reopen the card the keypress just dismissed.
  const skipNextFocusOpen = useRef(false);
  // @types/react is pinned to 16 in this repo, so `useId` is not available.
  const cardId = useMemo(() => `company-info-${(cardCounter += 1)}`, []);

  const isOpen = Boolean(anchor);

  const clearTimers = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const scheduleOpen = (element: HTMLElement) => {
    clearTimers();
    openTimer.current = setTimeout(() => setAnchor(element), OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setAnchor(null), CLOSE_DELAY_MS);
  };

  const closeNow = () => {
    clearTimers();
    setAnchor(null);
  };

  // The trigger and the card are one control for focus purposes, so closing on any
  // blur would unmount the card the moment focus reached its links.
  const handleBlur = (event: React.FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget as Node | null;

    if (
      next &&
      (triggerRef.current?.contains(next) || cardRef.current?.contains(next))
    ) {
      clearTimers();
      return;
    }

    closeNow();
  };

  const returnFocusAndClose = () => {
    closeNow();

    if (triggerRef.current && document.activeElement !== triggerRef.current) {
      skipNextFocusOpen.current = true;
      triggerRef.current.focus();
    }
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      closeNow();
      return;
    }

    // The card is portalled to the end of <body>, so sequential focus would walk the
    // rest of the page before reaching it. Hand focus over explicitly instead.
    if (event.key === 'Tab' && !event.shiftKey && isOpen) {
      const first = cardRef.current?.querySelector<HTMLElement>(CARD_FOCUSABLE);

      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      returnFocusAndClose();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable =
      cardRef.current?.querySelectorAll<HTMLElement>(CARD_FOCUSABLE) ?? [];

    if (event.shiftKey && event.target === focusable[0]) {
      event.preventDefault();
      triggerRef.current?.focus();
    }

    // Past the card's last link, sequential focus would carry on from the end of <body>.
    // Hand it back to the trigger, so the next Tab moves on from where the card opened.
    if (!event.shiftKey && event.target === focusable[focusable.length - 1]) {
      event.preventDefault();
      returnFocusAndClose();
    }
  };

  return (
    <>
      <Trigger
        ref={triggerRef}
        tabIndex={0}
        // Keeps an ancestor's native title from popping up over the card.
        title=""
        aria-describedby={isOpen ? cardId : undefined}
        data-provides="company-info-trigger"
        onMouseEnter={(event: React.MouseEvent<HTMLSpanElement>) =>
          scheduleOpen(event.currentTarget)
        }
        onMouseLeave={scheduleClose}
        onFocus={(event: React.FocusEvent<HTMLSpanElement>) => {
          clearTimers();

          if (skipNextFocusOpen.current) {
            skipNextFocusOpen.current = false;
            return;
          }

          setAnchor(event.currentTarget);
        }}
        onBlur={handleBlur}
        onKeyDown={handleTriggerKeyDown}
      >
        {companyName}
      </Trigger>
      {anchor && (
        <OpenCard
          companyName={companyName}
          cardId={cardId}
          anchor={anchor}
          cardRef={cardRef}
          onMouseEnter={clearTimers}
          onMouseLeave={scheduleClose}
          onBlur={handleBlur}
          onKeyDown={handleCardKeyDown}
        />
      )}
    </>
  );
};

export default CompanyInfoHover;
